import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FeedFilters {
  selectedInterests: string[];
  appliedInstructorsOnly: boolean;
  activeTab: string;
}

interface FeedFilterContextType {
  filters: FeedFilters;
  setFilters: (filters: FeedFilters) => void;
  updateFilters: (updates: Partial<FeedFilters>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

const FeedFilterContext = createContext<FeedFilterContextType | undefined>(undefined);

const STORAGE_KEY = 'sossiety_feed_filters';

const defaultFilters: FeedFilters = {
  selectedInterests: [],
  appliedInstructorsOnly: false,
  activeTab: 'public',
};

export const FeedFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [filters, setFiltersState] = useState<FeedFilters>(defaultFilters);

  // Load filters from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFiltersState({ ...defaultFilters, ...parsed });
      }
    } catch (error) {
      console.error('Error loading feed filters from localStorage:', error);
    }
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving feed filters to localStorage:', error);
    }
  }, [filters]);

  const setFilters = (newFilters: FeedFilters) => {
    setFiltersState(newFilters);
  };

  const updateFilters = (updates: Partial<FeedFilters>) => {
    setFiltersState(prev => ({ ...prev, ...updates }));
  };

  const clearFilters = () => {
    setFiltersState({
      ...defaultFilters,
      activeTab: filters.activeTab, // Keep the current tab
    });
  };

  const hasActiveFilters = filters.selectedInterests.length > 0 || filters.appliedInstructorsOnly;

  const value: FeedFilterContextType = {
    filters,
    setFilters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
  };

  return (
    <FeedFilterContext.Provider value={value}>
      {children}
    </FeedFilterContext.Provider>
  );
};

export const useFeedFilters = (): FeedFilterContextType => {
  const context = useContext(FeedFilterContext);
  if (context === undefined) {
    throw new Error('useFeedFilters must be used within a FeedFilterProvider');
  }
  return context;
};


